import React, { createContext, useContext, useState, useEffect } from 'react';
import { api } from '../utils/api';

const AuthContext = createContext(null);

function decodeJwtPayload(token) {
  try { return JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))); }
  catch { return null; }
}

function getJiraTokenExpiresAt(token) {
  const payload = decodeJwtPayload(token);
  if (!payload?.iat) return null;
  if (payload.jiraId === 'local-admin') return null;
  return (payload.iat + 3600) * 1000;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [jiraTokenExpiresAt, setJiraTokenExpiresAt] = useState(null);
  const [jiraTokenMinutesLeft, setJiraTokenMinutesLeft] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const authError = params.get('auth_error');
    if (token) { localStorage.setItem('retro_token', token); window.history.replaceState({}, '', window.location.pathname); }
    if (authError) { console.error('Auth error:', authError); window.history.replaceState({}, '', window.location.pathname); }

    const saved = localStorage.getItem('retro_token');
    if (saved) {
      setJiraTokenExpiresAt(getJiraTokenExpiresAt(saved));
      api.auth.me().then(setUser).catch(() => { localStorage.removeItem('retro_token'); setUser(null); }).finally(() => setLoading(false));
    } else { setLoading(false); }
  }, []);

  useEffect(() => {
    if (!jiraTokenExpiresAt) return;
    let hasRefreshed = false;
    const tick = async () => {
      const msLeft = jiraTokenExpiresAt - Date.now();
      const minsLeft = Math.max(0, Math.floor(msLeft / 60000));
      setJiraTokenMinutesLeft(minsLeft);
      if (minsLeft <= 5 && minsLeft > 0 && !hasRefreshed) {
        hasRefreshed = true;
        try {
          const result = await api.auth.refresh();
          localStorage.setItem('retro_token', result.token);
          setJiraTokenExpiresAt(getJiraTokenExpiresAt(result.token));
          hasRefreshed = false;
        } catch (e) { console.warn('[AUTH] Refresh failed:', e.message); }
      }
    };
    tick();
    const interval = setInterval(tick, 30000);
    return () => clearInterval(interval);
  }, [jiraTokenExpiresAt]);

  const login = () => { localStorage.setItem("retro_return_url", window.location.href); window.location.href = api.auth.loginUrl(); };
  const logout = () => { localStorage.removeItem('retro_token'); setUser(null); setJiraTokenExpiresAt(null); setJiraTokenMinutesLeft(null); };

  const localLogin = async (password, displayName) => {
    const data = await api.auth.localLogin(password, displayName);
    localStorage.setItem('retro_token', data.token);
    setJiraTokenExpiresAt(getJiraTokenExpiresAt(data.token));
    const userData = await api.auth.me();
    setUser(userData);
  };

  const jiraTokenWarning = user && jiraTokenMinutesLeft !== null && jiraTokenMinutesLeft <= 10;
  const jiraTokenExpired = user && jiraTokenMinutesLeft !== null && jiraTokenMinutesLeft <= 0;

  return (
    <AuthContext.Provider value={{ user, loading, login, localLogin, logout, isFacilitator: !!user, jiraTokenMinutesLeft, jiraTokenWarning, jiraTokenExpired }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() { return useContext(AuthContext); }
